// Typed-confirmation modal for genuinely destructive admin actions.
//
// The confirm button stays disabled until the operator types the exact
// confirmation phrase (e.g. "RESET SCT-04412") — native confirm() is the
// repo norm for routine deletes, but actions that take a customer's device
// offline deserve a deliberate, typo-proof gate. First consumer: the
// onboarding out-of-box reset. Built on ModalShell (dark tone) + console Btn.

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ModalShell } from './ModalShell';
import { Btn } from '../admin/console';

export interface TypedConfirmModalProps {
  open: boolean;
  onClose: () => void;
  /** Runs only after the phrase matches and the confirm button is pressed. */
  onConfirm: () => void | Promise<void>;
  title: ReactNode;
  /** Explanation of consequences — shown above the phrase input. */
  children: ReactNode;
  /** Exact phrase the operator must type, e.g. `RESET SCT-04412`. */
  confirmPhrase: string;
  confirmLabel?: string;
  /** Disables everything while the action runs. */
  busy?: boolean;
}

export function TypedConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmPhrase,
  confirmLabel = 'Confirm',
  busy = false,
}: TypedConfirmModalProps) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // A fresh open must never inherit a previous confirmation.
  useEffect(() => {
    if (open) setTyped('');
  }, [open, confirmPhrase]);

  const matches = typed.trim() === confirmPhrase;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      tone="dark"
      closeDisabled={busy}
      closeOnBackdrop={!busy}
      closeOnEscape={!busy}
      initialFocusRef={inputRef as React.MutableRefObject<HTMLElement | null>}
      footer={
        <div className="flex justify-end gap-3">
          <Btn variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Btn>
          <Btn
            variant="dangerSolid"
            disabled={!matches || busy}
            onClick={() => void onConfirm()}
            data-testid="typed-confirm-submit"
          >
            {busy ? 'Working…' : confirmLabel}
          </Btn>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-slate-300">
        {children}
        <div>
          <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1.5">
            Type <span className="font-mono text-red-400 select-all">{confirmPhrase}</span> to confirm
          </label>
          <input
            ref={inputRef}
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            placeholder={confirmPhrase}
            className="w-full px-3 py-2 bg-slate-900/60 border border-slate-600 rounded-lg text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>
    </ModalShell>
  );
}

export default TypedConfirmModal;
